import React from "react";
import dayjs from "dayjs";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { LeaveRequest } from "../types";
import { STATUS_CHIP, dateRangeLabel, daysLabel, typeLabel } from "../leaveFormat";

const headCellSx = { fontWeight: 600, py: 1, bgcolor: "grey.50", whiteSpace: "nowrap" };

type Props = {
  rows: LeaveRequest[];
  loading: boolean;
  /** admin list: show who applied */
  showApplicant?: boolean;
  /** shown when there are no rows */
  empty: React.ReactNode;
  /** buttons for a row (Cancel / Approve / Reject) */
  actions?: (leave: LeaveRequest) => React.ReactNode;
};

/** Leave requests as a compact table: dates, days, periods affected, type, reason, status and decision. */
export const LeaveTable: React.FC<Props> = ({ rows, loading, showApplicant, empty, actions }) => {
  const columns = 6 + (showApplicant ? 1 : 0) + (actions ? 1 : 0);
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small" sx={{ minWidth: showApplicant ? 900 : 760 }}>
        <TableHead>
          <TableRow>
            {showApplicant && <TableCell sx={headCellSx}>Faculty</TableCell>}
            <TableCell sx={headCellSx}>Dates</TableCell>
            <TableCell sx={headCellSx}>Days</TableCell>
            <TableCell sx={headCellSx}>Periods</TableCell>
            <TableCell sx={headCellSx}>Type</TableCell>
            <TableCell sx={headCellSx}>Reason</TableCell>
            <TableCell sx={headCellSx}>Status</TableCell>
            {actions && <TableCell sx={headCellSx} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns} align="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns} align="center" sx={{ py: 6 }}>
                {empty}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((leave) => {
              const chip = STATUS_CHIP[leave.status];
              const decision = leave.decidedByName || leave.decisionNote;
              return (
                <TableRow key={leave.id} hover sx={{ "&:last-child td": { border: 0 }, "& td": { py: 1, verticalAlign: "top" } }}>
                  {showApplicant && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {leave.applicantName}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" fontWeight={500}>
                      {dateRangeLabel(leave)}
                    </Typography>
                    {leave.appliedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Applied {dayjs(leave.appliedAt).format("D MMM")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{daysLabel(leave.days)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color={leave.periodsAffected ? "text.primary" : "text.disabled"}>
                      {leave.periodsAffected}
                    </Typography>
                  </TableCell>
                  <TableCell>{typeLabel(leave.leaveType)}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Tooltip title={leave.reason} placement="bottom-start" enterDelay={400}>
                      <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {leave.reason}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Chip label={chip.label} color={chip.color} size="small" variant={leave.status === "CANCELLED" ? "outlined" : "filled"} sx={{ fontWeight: 600, height: 22 }} />
                    {decision && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {leave.decidedByName && `by ${leave.decidedByName}`}
                        {leave.decisionNote && `${leave.decidedByName ? ": " : ""}“${leave.decisionNote}”`}
                      </Typography>
                    )}
                  </TableCell>
                  {actions && (
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: "inline-flex", gap: 0.5 }}>{actions(leave)}</Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
